// User Dashboard — staff activity, as opposed to the Operations dashboard's view of the business.
//
// It answers "what did our people do": who booked orders (and on which courier), who marked
// shipments delivered or RTO-delivered from tracking, and what follow-up is outstanding against
// them. Defaults to TODAY because that is the question a supervisor actually asks during a shift;
// the calendar widens it.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users as UsersIcon, PackagePlus, PackageCheck, RotateCcw, ScanLine, FileWarning, ArrowRight,
} from 'lucide-react';
import { api } from '@/api/client';
import { DateRangePicker, DatePreset, DateRange } from '@/components/ui/DateRangePicker';

type OT = 'ALL' | 'B2C' | 'B2B';

interface UserRow {
  userId: number; name: string; empCode: string | null; userType: string | null;
  branch: string | null; isActive: boolean | null;
  booked: number; delivered: number; rtoDelivered: number; totalScans: number;
  podPending: number; lastActivityAt: string | null;
}
interface UserDash {
  range: { from: string; to: string };
  summary: {
    activeUsers: number; booked: number; bookingUsers: number;
    delivered: number; deliveredUsers: number; rtoDelivered: number; rtoUsers: number;
    totalScans: number; podPending: number;
    autoDelivered: number; autoRtoDelivered: number;
  };
  rows: UserRow[];
  userCourier: { userId: number; userName: string; courierId: number; courierName: string; orders: number }[];
  byCourier: { courierId: number; courierName: string; orders: number; users: number }[];
  scansByStatus: { statusId: number; status: string; count: number }[];
  podPendingOrders: {
    orderId: string; orderNo: string; awbNo: string | null; orderType: string;
    customerName: string; customerCity: string; deliveredAt: string | null; markedByUserId: number | null;
  }[];
}

const todayIso = () => new Date().toISOString().slice(0, 10);

export function UserDashboardPage() {
  const [range, setRange] = useState<DateRange>({ startDate: todayIso(), endDate: todayIso() });
  const [preset, setPreset] = useState<DatePreset>('today');
  const [orderType, setOrderType] = useState<OT>('ALL');
  // Clicking a user filters every panel to that person, so "why is this number high" is one click
  // rather than a separate screen.
  const [focusUser, setFocusUser] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery<UserDash>({
    queryKey: ['dashboard', 'users', range.startDate, range.endDate, orderType, focusUser],
    queryFn: async () => (await api.get('/dashboard/users', {
      params: {
        from: range.startDate, to: range.endDate,
        ...(orderType !== 'ALL' ? { orderType } : {}),
        ...(focusUser ? { userId: focusUser } : {}),
      },
    })).data,
  });

  const focusName = useMemo(
    () => data?.rows.find((r) => r.userId === focusUser)?.name ?? null,
    [data, focusUser],
  );
  const userNameById = useMemo(() => {
    const m = new Map<number, string>();
    (data?.rows ?? []).forEach((r) => m.set(r.userId, r.name));
    return m;
  }, [data]);

  const s = data?.summary;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">User Dashboard</h1>
          <p className="text-sm text-slate-500">
            Who booked, who marked delivered / returned, and what is still open against them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-sm">
            {(['ALL', 'B2C', 'B2B'] as OT[]).map((t) => (
              <button key={t} onClick={() => setOrderType(t)}
                className={`px-3 py-1.5 ${orderType === t ? 'bg-primary text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
                {t}
              </button>
            ))}
          </div>
          <DateRangePicker
            value={range}
            defaultPreset={preset}
            onChange={(r) => { setRange({ startDate: r.startDate, endDate: r.endDate }); setPreset(r.preset); }}
          />
        </div>
      </div>

      {focusUser && (
        <div className="flex items-center gap-2 text-sm bg-primary/5 border border-primary/20 rounded-lg px-3 py-2">
          <span className="text-slate-700">Showing only <b>{focusName ?? `user ${focusUser}`}</b></span>
          <button onClick={() => setFocusUser(null)} className="text-primary underline text-xs">Show everyone</button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">
          {(error as any)?.response?.data?.message ?? 'Could not load the user dashboard'}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile icon={<UsersIcon size={16} />} label="Active Users" value={s?.activeUsers} loading={isLoading}
          hint="booked or scanned in this period" />
        <Tile icon={<PackagePlus size={16} />} label="Orders Booked" value={s?.booked} loading={isLoading}
          hint={s ? `by ${s.bookingUsers} user${s.bookingUsers === 1 ? '' : 's'}` : undefined} />
        {/* The hint carries the courier-recorded count alongside the staff one. Most RTO-delivered
            events arrive from the courier's own tracking, so "0 marked by users" is usually the
            truth rather than a fault — saying so on the tile stops it reading as broken. */}
        <Tile icon={<PackageCheck size={16} />} label="Marked Delivered" value={s?.delivered} loading={isLoading}
          hint={s ? `by ${s.deliveredUsers} user${s.deliveredUsers === 1 ? '' : 's'} · ${s.autoDelivered} from courier` : undefined}
          tone="emerald" />
        <Tile icon={<RotateCcw size={16} />} label="Marked RTO Delivered" value={s?.rtoDelivered} loading={isLoading}
          hint={s ? `by ${s.rtoUsers} user${s.rtoUsers === 1 ? '' : 's'} · ${s.autoRtoDelivered} from courier` : undefined}
          tone="amber" />
        <Tile icon={<ScanLine size={16} />} label="Total Scans" value={s?.totalScans} loading={isLoading}
          hint="every manual tracking scan" />
        <Tile icon={<FileWarning size={16} />} label="POD Pending" value={s?.podPending} loading={isLoading}
          hint="delivered on Gaint network, no POD" tone={s && s.podPending > 0 ? 'rose' : undefined} />
      </div>

      <Panel title="Activity by user" subtitle="Click a row to filter everything below to that person">
        <Table
          head={['User', 'Emp Code', 'Branch', 'Booked', 'Delivered', 'RTO Del.', 'Scans', 'POD Pending', 'Last Activity']}
          align={['left', 'left', 'left', 'right', 'right', 'right', 'right', 'right', 'left']}
          loading={isLoading}
          empty="Nobody booked or scanned anything in this period."
          rows={(data?.rows ?? []).map((r) => ({
            key: r.userId,
            onClick: () => setFocusUser(focusUser === r.userId ? null : r.userId),
            active: focusUser === r.userId,
            cells: [
              <span className="font-medium text-slate-800">
                {r.name}
                {r.isActive === false && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">inactive</span>}
              </span>,
              r.empCode ?? '—',
              r.branch ?? '—',
              r.booked || '—',
              <span className={r.delivered ? 'text-emerald-700 font-medium' : ''}>{r.delivered || '—'}</span>,
              <span className={r.rtoDelivered ? 'text-amber-700 font-medium' : ''}>{r.rtoDelivered || '—'}</span>,
              r.totalScans || '—',
              <span className={r.podPending ? 'text-rose-700 font-medium' : ''}>{r.podPending || '—'}</span>,
              r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleString('en-IN') : '—',
            ],
          }))}
        />
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Panel title="Bookings by courier" subtitle="How many orders went out on each courier, and how many people booked them">
          <Table
            head={['Courier', 'Orders', 'Users']}
            align={['left', 'right', 'right']}
            loading={isLoading}
            empty="No bookings in this period."
            rows={(data?.byCourier ?? []).map((c) => ({
              key: c.courierId,
              cells: [c.courierName, c.orders, c.users],
            }))}
          />
        </Panel>

        <Panel title="User × courier" subtitle="Who booked what, courier by courier">
          <Table
            head={['User', 'Courier', 'Orders']}
            align={['left', 'left', 'right']}
            loading={isLoading}
            empty="No bookings in this period."
            maxHeight
            rows={(data?.userCourier ?? []).map((r, i) => ({
              key: `${r.userId}-${r.courierId}-${i}`,
              cells: [r.userName, r.courierName, r.orders],
            }))}
          />
        </Panel>
      </div>

      <Panel title="Scans by status" subtitle="The full shape of manual tracking work, not just the terminal statuses">
        <Table
          head={['Status', 'Scans']}
          align={['left', 'right']}
          loading={isLoading}
          empty="No manual scans in this period."
          maxHeight
          rows={(data?.scansByStatus ?? []).map((r) => ({ key: r.statusId, cells: [r.status, r.count] }))}
        />
      </Panel>

      <Panel
        title="POD pending — delivered on the Gaint network with no proof on file"
        subtitle="Third-party carriers are excluded on purpose: their POD is generated on demand, so nothing is outstanding there."
      >
        <Table
          head={['Order No', 'AWB / LR', 'Type', 'Consignee', 'City', 'Delivered', 'Marked By', '']}
          align={['left', 'left', 'left', 'left', 'left', 'left', 'left', 'right']}
          loading={isLoading}
          empty="No missing PODs in this period — everything delivered on our own network has proof on file."
          maxHeight
          rows={(data?.podPendingOrders ?? []).map((o) => ({
            key: o.orderId,
            cells: [
              o.orderNo,
              <span className="font-mono text-xs">{o.awbNo ?? '—'}</span>,
              o.orderType,
              o.customerName,
              o.customerCity,
              o.deliveredAt ? new Date(o.deliveredAt).toLocaleDateString('en-IN') : '—',
              o.markedByUserId ? (userNameById.get(o.markedByUserId) ?? `User ${o.markedByUserId}`) : '—',
              <Link to={`/orders/${o.orderId}`} className="text-primary inline-flex items-center gap-1 text-xs">
                Open <ArrowRight size={12} />
              </Link>,
            ],
          }))}
        />
        {(data?.podPendingOrders?.length ?? 0) >= 500 && (
          <div className="text-xs text-amber-700 mt-2">
            Showing the newest 500 only — narrow the date range to see the rest.
          </div>
        )}
      </Panel>
    </div>
  );
}

const TONES: Record<string, string> = {
  emerald: 'text-emerald-700',
  amber: 'text-amber-700',
  rose: 'text-rose-700',
};

function Tile({ icon, label, value, hint, loading, tone }: {
  icon: React.ReactNode; label: string; value?: number; hint?: string; loading?: boolean; tone?: string;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">{icon}{label}</div>
      <div className={`text-2xl font-bold mt-1 ${tone ? TONES[tone] ?? 'text-slate-900' : 'text-slate-900'}`}>
        {loading ? <span className="text-slate-300">—</span> : (value ?? 0).toLocaleString('en-IN')}
      </div>
      {hint && <div className="text-[11px] text-slate-400 mt-0.5">{hint}</div>}
    </div>
  );
}

function Panel({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="mb-3">
        <h2 className="text-sm font-semibold text-slate-800">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function Table({ head, align, rows, loading, empty, maxHeight }: {
  head: string[];
  align: ('left' | 'right')[];
  rows: { key: React.Key; cells: React.ReactNode[]; onClick?: () => void; active?: boolean }[];
  loading?: boolean;
  empty: string;
  maxHeight?: boolean;
}) {
  if (loading) return <div className="text-sm text-slate-400 py-6 text-center">Loading…</div>;
  if (rows.length === 0) return <div className="text-sm text-slate-400 py-6 text-center">{empty}</div>;
  return (
    // Wide tables scroll inside their own box so the page body never scrolls sideways.
    <div className={`overflow-x-auto ${maxHeight ? 'max-h-80 overflow-y-auto' : ''}`}>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-slate-500 sticky top-0 bg-white">
          <tr>{head.map((h, i) => <th key={i} className={`py-1.5 px-2 text-${align[i] ?? 'left'} whitespace-nowrap`}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}
              onClick={r.onClick}
              className={`border-t border-slate-100 ${r.onClick ? 'cursor-pointer hover:bg-slate-50' : ''} ${r.active ? 'bg-primary/5' : ''}`}>
              {r.cells.map((c, i) => (
                <td key={i} className={`py-2 px-2 text-${align[i] ?? 'left'} whitespace-nowrap`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
